/**
 * Hook da projeção de PLD (Task #209 — consome /api/v2/pld/projecao, backend #208).
 *
 * Mesmo padrão do usePldOverview: cache module-level KEYED por (mes_ref,
 * submercado), refetch ao trocar params, reset de loading. TTL 5 min — a
 * projeção é compute-on-request (sem materialização) e muda quando entra novo
 * realizado / nova REV DECOMP.
 */
import { useEffect, useState } from "react";
import { getPldProjecao } from "../api/client";
import type { PldProjecaoResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 5 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<PldProjecaoResponse>;
}

const cache = new Map<string, CacheEntry>();

export function invalidatePldProjecaoCache(): void {
  cache.clear();
}

export interface UsePldProjecaoState {
  data: PldProjecaoResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function usePldProjecao(
  mesRef: string,
  submercado: string,
  /** Task #210: bump pra forçar refetch (ex.: após inserir nova REV DECOMP).
   * Combinar com invalidatePldProjecaoCache() pra furar o cache. */
  reloadKey: number = 0,
): UsePldProjecaoState {
  const [state, setState] = useState<UsePldProjecaoState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    const key = `${mesRef}|${submercado}`;

    function load() {
      const now = Date.now();
      let entry = cache.get(key);
      if (!entry || entry.expiresAt < now) {
        entry = {
          expiresAt: now + REFETCH_MS,
          promise: getPldProjecao(mesRef, submercado),
        };
        cache.set(key, entry);
      }
      entry.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key);
          setState({ data: null, isLoading: false, error: err });
        });
    }

    setState((s) => ({ ...s, isLoading: true }));
    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [mesRef, submercado, reloadKey]);

  return state;
}
